import React, { Component } from 'react';

import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  split,
  HttpLink,
  //makeVar
} from '@apollo/client';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from '@apollo/client/link/ws';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import possibleTypes from '@betwixt/my/src/possibleTypes.json';

import Loading from '@betwixt/common/components/Loading.js';
import { getTypePolicies } from '@betwixt/common/utils/modules.js';
import setTokenFromURL from '@betwixt/common/utils/setTokenFromURL.js';

import { message } from 'antd';
import DEVConsole from '@betwixt/common/utils/DEVConsole.js';

import gConfig from '@betwixt/common/config/config.js';
import App from './App.js';
/*
 * Cache
 */
class Cache extends Component {
  constructor(props) {
    super(props);

    this.state = {
      client: null,
      loaded: false,
    };
  }

  componentDidMount = async () => {
    setTokenFromURL();

    const middlewareLink = setContext(() => ({
      headers: {
        'x-token': localStorage.getItem('token'),
        'x-refresh-token': localStorage.getItem('refreshToken'),
      },
    }));

    const afterwareLink = new ApolloLink((operation, forward) => {
      return forward(operation).map((response) => {
        const context = operation.getContext();
        const responseHeaders = context && context.response ? context.response.headers : null;

        if (responseHeaders && responseHeaders.get) {
          const token = responseHeaders.get('x-token');
          const refreshToken = responseHeaders.get('x-refresh-token');

          if (token) {
            localStorage.setItem('token', token);
          }

          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
          }
        }

        return response;
      });
    });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      DEVConsole.log('**** errorLink : ', graphQLErrors, networkError);

      if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }) =>
          DEVConsole.log(
            `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
          )
        );

      if (networkError) {
        message.error(`Network error: ${networkError.message}`);
        DEVConsole.log(`[Network error]: ${networkError}`);
      }

      if (process) {
        if (process.env.NODE_ENV !== 'development') {
          //OAuth.logout();
          DEVConsole.log(graphQLErrors, networkError);
        } else {
          DEVConsole.log('logout user on production/test environment');
          DEVConsole.log(graphQLErrors, networkError);
          alert('[GraphQL error]');
        }
      }
    });

    const httpLink = new HttpLink({
      uri: gConfig.GraphQL.uri,
    });

    // Create WebSocket link using apollo-link-ws and subscriptions-transport-ws

    if (process.env.NODE_ENV === 'development') {
      DEVConsole.log('=== CLIENT WEBSOCKET SETUP ===');
      DEVConsole.log('WebSocket URI:', gConfig.WebSocket.uri);
      DEVConsole.log('Token available:', !!localStorage.getItem('token'));
    }

    const wsClient = new SubscriptionClient(gConfig.WebSocket.uri, {
      reconnect: true,
      connectionParams: () => {
        const params = {
          'x-token': localStorage.getItem('token'),
          'x-refresh-token': localStorage.getItem('refreshToken'),
        };
        DEVConsole.log('WebSocket connection params:', {
          hasToken: !!params['x-token'],
          hasRefreshToken: !!params['x-refresh-token'],
        });
        return params;
      },
      onConnected: () => {
        DEVConsole.log('=== CLIENT WEBSOCKET CONNECTED ===');
        DEVConsole.log('WebSocket ready state:', wsClient.client.readyState);
      },
      onDisconnected: () => {
        DEVConsole.log('=== CLIENT WEBSOCKET DISCONNECTED ===');
        DEVConsole.log(
          'WebSocket ready state:',
          wsClient.client ? wsClient.client.readyState : 'no client'
        );
      },
      onError: (error) => {
        console.error('=== CLIENT WEBSOCKET ERROR ===');
        console.error('WebSocket error:', error);
      },
      onReconnected: () => {
        DEVConsole.log('=== CLIENT WEBSOCKET RECONNECTED ===');
      },
    });

    const wsLink = new WebSocketLink(wsClient);
    const httpLinkExtended = errorLink.concat(
      afterwareLink.concat(middlewareLink.concat(httpLink))
    );

    const link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      httpLinkExtended
    );

    const typePolicies = await getTypePolicies();

    let cache = new InMemoryCache({
      possibleTypes,
      typePolicies,
    });

    const client = new ApolloClient({
      cache: cache,
      link: link,
      devtools: {
        enabled: gConfig.config_id === 'development',
      },
    });

    localStorage.removeItem('SRLoadedModules');

    this.setState({
      client,
      loaded: true,
    });

    if (process.env.NODE_ENV === 'development') {
      // Adds messages only in a dev environment
      loadDevMessages();
      loadErrorMessages();
    }
  };

  render() {
    const { client, loaded } = this.state;

    if (!loaded) {
      return <Loading type="ring" />;
    }

    return (
      <ApolloProvider client={client}>
        <App gConfig={gConfig} />
      </ApolloProvider>
    );
  }
}

export default Cache;
